import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  authorityAdvanceBatchEhrlich,
  ApiRequestError,
  isAbortError,
  jfetch,
  type BatchAdvanceResponse,
  type GateBundleView,
  type OwnerGateView,
} from "../api";
import { isTimeoutError } from "../resourceState";

/**
 * CP-12 — Human Gate Inbox: erste Klasse Operator-Inbox für echte
 * Owner-Entscheidungen. Der Owner ruft NIEMALS manuell
 * `POST /api/human-gates/...` — die Inbox macht genau das über die echte
 * Resolve-Route inkl. Nonce/Replay-Schutz (Server-Autorität unangetastet,
 * kein Client-seitiger Bypass: ohne gültige Session/Nonce kein Schreiben).
 *
 * Je Bundle: warum die Entscheidung existiert, betroffene Mission/Items,
 * Empfehlung (exact_action), bereits autonom versuchte Alternativen,
 * Konsequenzen (risk + what_continues_after), Expiry.
 * Aktionen: Freigeben (RESOLVED) / Zurückziehen-Parken (WITHDRAWN).
 */

export function HumanGateInbox({ onChanged }: { onChanged?: () => void }): JSX.Element {
  // LOADING-TRUTH-FIX (S4 / NO_FALSE_ZERO): `bundles` bleibt null, bis eine
  // autoritativ erfolgreiche Antwort vorliegt. Der Header-Zaehler zeigt
  // "—" bei LOADING/SOURCE_UNAVAILABLE/ERROR/UNKNOWN und "0" NUR bei
  // EMPTY_PROVEN (erfolgreiche, leere Antwort) — nie eine Fake-0.
  const [bundles, setBundles] = useState<GateBundleView[] | null>(null);
  // S0.1: Entscheidungs-Karten fuer AUTHORITY_ADVANCE kommen aus dem
  // Owner-Gate-Lesepfad (Bundle-Snapshot mit missions/familyKey).
  const [ownerGates, setOwnerGates] = useState<OwnerGateView[] | null>(null);
  const [loading, setLoading] = useState(true);
  // S0.3 — KLARTEXT STATT ROHFEHLER: klassifizierter Fehlerzustand. Der rohe
  // AbortError-String ('The user aborted a request.') war am 2026-09-18 als
  // 'Inbox-Fehler' in der Owner-UI gelandet; das Original steht nur noch
  // unter einklappbaren Details.
  const [fehler, setFehler] = useState<{ art: "TIMEOUT" | "FEHLER"; text: string; detail: string } | null>(null);
  /** S0.3: TRUE, wenn der angezeigte Stand der letzte ERFOLG ist (bei TIMEOUT). */
  const [veraltet, setVeraltet] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string>("");
  // S0.3 — REFRESH-DISZIPLIN: EIN autoritative Ladung je Ressource. Neuer
  // Load superseded den alten (AbortController) — dessen Abbruch-Fehler wird
  // STILL verworfen; Spaet-Antworten verwirft der Sequence-Guard; 30s-Takt,
  // manueller Refresh und Route-Reaktivierung koaleszieren (kein Sturm).
  const loadSeqRef = useRef(0);
  const loadAbortRef = useRef<AbortController | null>(null);
  const loadStartRef = useRef(0);

  const load = useCallback((): void => {
    if (loadAbortRef.current !== null && Date.now() - loadStartRef.current < 5_000) {
      return; // Coalescing: identische Ladung laeuft noch frisch
    }
    const seq = ++loadSeqRef.current;
    const istAktuell = (): boolean => loadSeqRef.current === seq;
    loadAbortRef.current?.abort(); // Supersede: der ALTE Request-Fehler ist nie Anzeige-Zustand
    const controller = new AbortController();
    loadAbortRef.current = controller;
    loadStartRef.current = Date.now();
    setLoading(true);
    setFehler(null);
    api
      .gateBundles({ signal: controller.signal })
      .then((r) => {
        if (!istAktuell()) return;
        setBundles(r.bundles);
        setVeraltet(false);
      })
      .catch((e) => {
        if (!istAktuell() || isAbortError(e)) return; // Supersede/externer Abort: still
        if (isTimeoutError(e)) {
          setFehler({
            art: "TIMEOUT",
            text: "Freigaben konnten nicht rechtzeitig geladen werden.",
            detail: e instanceof Error ? e.message : String(e),
          });
          setVeraltet(true); // letzter Erfolgssnapshot bleibt mit STALE-Kennzeichnung stehen
        } else {
          setFehler({
            art: "FEHLER",
            text: "Freigaben konnten nicht geladen werden.",
            detail: e instanceof Error ? e.message : String(e),
          });
        }
      })
      .finally(() => {
        if (istAktuell()) setLoading(false);
        if (loadAbortRef.current === controller) loadAbortRef.current = null;
      });
    api
      .ownerGateStatus({ signal: controller.signal })
      .then((r) => {
        if (!istAktuell()) return;
        // WARMING (S0.3): Aufwaermphase des Server-Read-Models — bewusst null
        // ("—" im Header), NIEMALS ein Inbox-Fehler und keine Fake-0.
        setOwnerGates(r.state === "LIVE" ? (r.bundles ?? []) : null);
      })
      .catch(() => {
        if (istAktuell()) setOwnerGates(null);
      });
  }, []);
  useEffect(load, [load]);

  const resolve = (bundle: GateBundleView, entscheidung: "RESOLVED" | "WITHDRAWN"): void => {
    setBusy(bundle.bundleId);
    setFehler(null);
    const authorizationRef = hinweis.trim() || `web-inbox:${new Date().toISOString()}`;
    jfetch<{ ok: boolean; resolved: number }>(`/api/human-gates/${encodeURIComponent(bundle.bundleId)}/resolve`, {
      method: "POST",
      body: JSON.stringify({
        nonce: bundle.nonce,
        authorizationRef,
        decisions: Object.fromEntries(bundle.gates.map((g) => [g.gateId, entscheidung])),
      }),
    })
      .then(() => {
        setHinweis("");
        load();
        onChanged?.();
      })
      .catch((e) =>
        setFehler({
          art: "FEHLER",
          text: "Die Entscheidung konnte nicht gespeichert werden.",
          detail: e instanceof Error ? e.message : String(e),
        }),
      )
      .finally(() => setBusy(null));
  };

  return (
    <div className="card border-rose-500/30 p-3 sm:p-4" data-testid="human-gate-inbox" aria-label="Human Gate Inbox">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold uppercase tracking-wider text-xs text-rose-300" data-testid="human-gate-inbox-count">
          {/* NO_FALSE_ZERO (S4): "0" nur bei EMPTY_PROVEN (autoritativ
              erfolgreiche, leere Antwort); sonst "—" statt Fake-0. */}
          Owner-Aktion erforderlich ({bundles === null ? "—" : bundles.length})
        </h3>
        <button className="btn" onClick={load} disabled={loading}>
          {loading ? "Lade…" : "Aktualisieren"}
        </button>
      </div>

      {fehler && (
        <div className="text-rose-300 text-sm mb-3" role="alert" data-testid="human-gate-inbox-error">
          <span>
            {fehler.text}
            {veraltet && bundles !== null && bundles.length > 0 ? " (angezeigter Stand: veraltet)" : ""}
          </span>{" "}
          <button className="btn ml-2 py-0.5 text-xs" onClick={load} data-testid="human-gate-inbox-retry">
            Erneut versuchen
          </button>
          <details className="mt-1">
            <summary className="cursor-pointer text-xs text-jarvis-muted">Details</summary>
            <span className="text-xs text-jarvis-muted break-all">{fehler.detail}</span>
          </details>
        </div>
      )}

      {bundles !== null && bundles.length === 0 && !loading && !fehler && (
        <div className="text-sm text-jarvis-muted">
          Keine offenen Owner-Entscheidungen. Normale Wartezustände (Ressource/Dependency/Scheduling) erscheinen hier
          bewusst nicht — nur echte Human Gates.
        </div>
      )}

      {bundles !== null && bundles.length > 0 && (
        <input
          className="input w-full text-sm mb-3"
          placeholder="Optionale Begründung für die Entscheidung (Authorization-Ref)…"
          value={hinweis}
          onChange={(e) => setHinweis(e.target.value)}
          aria-label="Entscheidungs-Begründung"
        />
      )}

      <div className="space-y-3">
        {/* S0.1: Eine Karte je Familie/Einzelmission — aus dem eingefrorenen
            Bundle-Snapshot des Owner-Gate-Lesepfads (kein Global-Batch mehr). */}
        {(ownerGates ?? []).map((g) => (
          <OwnerGateAdvanceCard key={g.gateId} view={g} onAktualisieren={() => { load(); onChanged?.(); }} />
        ))}
        {(bundles ?? [])
          .filter((b) => !(ownerGates && b.gates.length > 0 && b.gates.every((g) => g.actionClass === "AUTHORITY_ADVANCE")))
          .map((b) => (
              <div key={b.bundleId} className="rounded border border-rose-500/25 bg-rose-500/5 p-3" data-testid={`gate-bundle-${b.bundleId}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <span className="font-mono text-[10px] text-jarvis-muted">
                    Mission {b.missionId} · gültig bis {new Date(b.freshUntil).toLocaleString("de-DE")}
                  </span>
                  <span className="rounded-full border border-rose-500/40 bg-rose-500/20 px-2 py-0.5 text-[10px] font-medium text-rose-300">
                    {b.gates.length} Entscheidung{b.gates.length > 1 ? "en" : ""}
                  </span>
                </div>
                {b.gates.map((g) => (
                  <div key={g.gateId} className="mt-2 space-y-1 text-sm" data-testid={`gate-detail-${g.gateId}`}>
                    <div className="font-medium">{g.exactAction}</div>
                    <div className="text-xs text-jarvis-muted space-y-0.5">
                      <div><span className="text-jarvis-text/80">Warum nötig:</span> {g.whyRequired}</div>
                      <div><span className="text-jarvis-text/80">Risiko:</span> {g.risk}</div>
                    </div>
                    {/* EMBER_CALM §5 — Guided Decision: Konsequenzen BEIDER Wege
                        vor der Entscheidung, keine künstliche Empfehlung (nur
                        evidence-backed — hier liegt keine vor). */}
                    <div className="grid gap-2 sm:grid-cols-2 text-xs mt-1">
                      <div className="rounded border border-emerald-500/25 bg-emerald-500/5 p-2">
                        <div className="font-medium text-emerald-300">Wenn du freigibst</div>
                        <div className="text-jarvis-muted mt-0.5">{g.whatContinuesAfter}</div>
                        <div className="text-jarvis-muted mt-0.5">Die Ausführung startet automatisch — kein weiterer Schritt nötig.</div>
                      </div>
                      <div className="rounded border border-jarvis-border/50 bg-jarvis-bg/20 p-2">
                        <div className="font-medium">Wenn du ablehnst / zurückziehst</div>
                        <div className="text-jarvis-muted mt-0.5">
                          Die Entscheidung wird als zurückgezogen gebucht; die Queue fährt mit der kanonischen
                          Fortsetzungslogik des Missionsvertrags fort (kein Stillstand durch Ablehnung).
                        </div>
                      </div>
                    </div>
                    <details className="text-[11px] text-jarvis-muted">
                      <summary className="cursor-pointer">Technische Details</summary>
                      <div className="mt-1 space-y-0.5 font-mono text-[10px] break-all">
                        {g.alternativesTried && g.alternativesTried !== "[]" && (
                          <div>Autonom versucht: {g.alternativesTried}</div>
                        )}
                        <div>Gate {g.gateId}</div>
                        <div>{g.actorClass} · {g.actionClass} · Ziel: {g.targetRef}</div>
                        <div>Rollback: über den Missionsvertrag geregelt (Acceptance-Contract des Items).</div>
                      </div>
                    </details>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    className="btn-primary text-xs"
                    disabled={busy === b.bundleId}
                    onClick={() => resolve(b, "RESOLVED")}
                    data-testid={`resolve-${b.bundleId}`}
                  >
                    {busy === b.bundleId ? "…" : "Freigeben"}
                  </button>
                  <button
                    className="btn text-xs"
                    disabled={busy === b.bundleId}
                    onClick={() => resolve(b, "WITHDRAWN")}
                    data-testid={`withdraw-${b.bundleId}`}
                  >
                    Ablehnen / zurückziehen
                  </button>
                </div>
              </div>
          ),
        )}
      </div>
    </div>
  );
}

/**
 * S0.1 — Entscheidungs-Karte fuer EIN Owner-Gate-Bundle (Mission(en) in
 * SHADOW aufnehmen). Eine Karte je Familie/Einzelmission; die Titelzahl und
 * der CTA kommen aus dem EINGEFRORENEN Bundle-Snapshot (missionIds), nicht
 * aus einer Live-Neu berechnung. Ember Calm: Titel/Erklaerung/CTAs in
 * Besitzer-Sprache; aufklappbare "Betroffene Missionen" mit Kopfzeile und
 * (klein, monospace) Missions-Id; technische Begriffe nur unter
 * "Technische Details". Nach dem Klick: ehrliche Per-Mission-Ergebnisse.
 */
function OwnerGateAdvanceCard({
  view,
  onAktualisieren,
}: {
  view: OwnerGateView;
  onAktualisieren: () => void;
}): JSX.Element {
  const n = view.missionIds.length;
  const [busy, setBusy] = useState<"advance" | "hold" | null>(null);
  const [ergebnis, setErgebnis] = useState<BatchAdvanceResponse | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [stepUpNeeded, setStepUpNeeded] = useState(false);
  const [stepUpPassword, setStepUpPassword] = useState("");

  const cta = n === 1 ? "In SHADOW aufnehmen" : n === 2 ? "Beide in SHADOW aufnehmen" : `${n} Missionen in SHADOW aufnehmen`;

  // S0.5: Freigeben verschiebt Autorität — der Server verlangt dafuer die
  // SEC-2-Trust-Wurzel. Im Browser ist der Weg dazu der Passwort-Step-up
  // (derselbe zweite Faktor wie bei High-Risk-Approvals). Der Owner-Token
  // bleibt bewusst ausserhalb des Renderers.
  const advance = (): void => {
    setBusy("advance");
    setFehler(null);
    authorityAdvanceBatchEhrlich("Freigabe aus der Entscheidungs-Inbox", view.gateId)
      .then((r) => setErgebnis(r))
      .catch((e) => {
        if (e instanceof ApiRequestError && e.errorCode === "trust_root_required") {
          setStepUpNeeded(true);
          return;
        }
        setFehler(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setBusy(null));
  };

  const advanceMitStepUp = (): void => {
    if (!stepUpPassword.trim()) {
      setFehler("Bitte Owner-Passwort eingeben.");
      return;
    }
    setBusy("advance");
    setFehler(null);
    api
      .stepUp(stepUpPassword)
      .then(() => authorityAdvanceBatchEhrlich("Freigabe aus der Entscheidungs-Inbox (Step-up bestätigt)", view.gateId))
      .then((r) => {
        setStepUpPassword("");
        setStepUpNeeded(false);
        setErgebnis(r);
      })
      .catch((e) => {
        const text = e instanceof Error ? e.message : String(e);
        setFehler(
          /step_up_password_invalid|Passwort nicht/i.test(text)
            ? "Passwort nicht bestätigt. Bitte erneut versuchen."
            : text,
        );
      })
      .finally(() => setBusy(null));
  };

  const halten = (): void => {
    setBusy("hold");
    setFehler(null);
    api
      .authorityGateHold("Vorerst halten — Entscheidung zurückgestellt", view.gateId)
      .then(() => setErgebnis({ ok: false, gateStatus: "HELD", message: "Vorerst gehalten — kein Advance ausgeführt." }))
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(null));
  };

  return (
    <div className="rounded border border-amber-400/30 bg-amber-400/5 p-3" data-testid={`owner-gate-advance-${view.gateId}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="font-medium text-sm" data-testid="owner-gate-advance-titel">
          {n === 1 ? "1 Mission in SHADOW aufnehmen" : `${n} Missionen in SHADOW aufnehmen`}
        </div>
        <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-medium text-amber-200">
          Freigabe erforderlich
        </span>
      </div>
      <p className="mt-2 text-sm text-jarvis-muted" data-testid="owner-gate-advance-erklaerung">
        SHADOW ist der sichere Beobachtungsmodus. SIMO darf Runtime-Evidenz sammeln und mitschreiben,
        entscheidet aber noch nicht autonom. Kein Merge, kein Deploy, keine Kontrolluebergabe.
      </p>
      <details className="mt-2 text-sm" data-testid={`owner-gate-affected-${view.gateId}`}>
        <summary className="cursor-pointer text-jarvis-muted text-xs">
          Betroffene Missionen ({n})
        </summary>
        <div className="mt-1 space-y-0.5">
          {view.missions.map((m) => (
            <div key={m.id} className="flex flex-wrap items-baseline gap-2">
              <span className="text-sm">{m.headline || m.id}</span>
              <span className="font-mono text-[10px] text-jarvis-muted break-all">{m.id}</span>
            </div>
          ))}
        </div>
      </details>
      {stepUpNeeded && (
        <div className="mt-2 rounded border border-amber-400/40 bg-amber-400/5 p-2" data-testid={`owner-gate-stepup-${view.gateId}`}>
          <div className="text-sm text-amber-200">
            Diese Entscheidung verschiebt Autorität. Bitte bestätige sie mit deinem
            Owner-Passwort (gilt 5 Minuten für diese Sitzung).
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              type="password"
              className="input text-xs"
              value={stepUpPassword}
              onChange={(e) => setStepUpPassword(e.target.value)}
              placeholder="Owner-Passwort"
              aria-label="Owner-Passwort für die Freigabe"
              data-testid="owner-gate-stepup-input"
            />
            <button
              className="btn-primary text-xs"
              disabled={busy === "advance"}
              onClick={advanceMitStepUp}
              data-testid="owner-gate-stepup-cta"
            >
              {busy === "advance" ? "…" : "Bestätigen & freigeben"}
            </button>
            <button
              className="btn text-xs"
              disabled={busy === "advance"}
              onClick={() => {
                setStepUpNeeded(false);
                setStepUpPassword("");
                setFehler(null);
              }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}
      {fehler && (
        <div className="text-rose-300 text-sm mt-2" role="alert">
          Fehler: {fehler}
        </div>
      )}
      {ergebnis && (
        <div className="mt-3 space-y-1 text-sm" data-testid="owner-gate-advance-ergebnis">
          {(ergebnis.results ?? []).map((r) => (
            <div key={r.missionId} className={r.ok ? "text-emerald-300" : "text-rose-300"}>
              {r.ok ? "Erfolg" : "Fehlgeschlagen"}: {r.missionId}
              {r.ok && r.receiptId ? ` — Übergang ${r.previous} → ${r.new} protokolliert` : ""}
              {!r.ok && r.message ? ` — ${r.message}` : ""}
            </div>
          ))}
          {(!ergebnis.results || ergebnis.results.length === 0) && ergebnis.message && (
            <div className="text-amber-200">{ergebnis.message}</div>
          )}
          <button className="btn text-xs mt-1" onClick={onAktualisieren} data-testid="owner-gate-advance-refresh">
            Ansicht aktualisieren
          </button>
        </div>
      )}
      <details className="text-[11px] text-jarvis-muted mt-2">
        <summary className="cursor-pointer">Technische Details</summary>
        <div className="mt-1 space-y-0.5 font-mono text-[10px] break-all">
          <div>Bundle {view.gateId} · {n} Mission(en) · Familie {view.familyKey}</div>
          {view.missionIds.map((id) => (
            <div key={id}>Mission {id} · {view.currentAuthorityState} → {view.nextAuthorityState}</div>
          ))}
        </div>
      </details>
      <div className="flex flex-wrap gap-2 mt-3">
        <button
          className="btn-primary text-xs"
          disabled={busy !== null}
          onClick={advance}
          data-testid={`owner-gate-advance-cta-${view.gateId}`}
        >
          {busy === "advance" ? "…" : cta}
        </button>
        <button
          className="btn text-xs"
          disabled={busy !== null}
          onClick={halten}
          data-testid={`owner-gate-hold-cta-${view.gateId}`}
        >
          {busy === "hold" ? "…" : "Vorerst halten"}
        </button>
      </div>
    </div>
  );
}
